// Keep this module independent of the store/API: tracing must never start work of its own.
export const OPERATION_PHASES = [
    'started',
    'composition_start',
    'composition_end',
    'composition_flush_begin',
    'composition_flush_end',
    'share_lookup_begin',
    'share_lookup_end',
    'post_fetch_begin',
    'post_fetch_end',
    'share_cache_begin',
    'share_cache_end',
    'resolve_begin',
    'resolve_end',
    'cache_update_begin',
    'cache_update_end',
    'embed_commit',
    'interaction',
    'upload_begin',
    'upload_end',
    'submit_begin',
    'validation_end',
    'embed_wait_begin',
    'embed_wait_end',
    'preupload_begin',
    'preupload_end',
    'optimistic_commit',
    'request_begin',
    'request_end',
    'complete',
    'blocked',
    'failed',
    'cancelled',
] as const

type Phase = (typeof OPERATION_PHASES)[number]
export type ComposerKind = 'modal' | 'inline' | 'unknown'
export type OperationKind = 'composer_link_preview' | 'composer_media_upload' | 'composer_submit'
export type MediaKind = 'none' | 'image' | 'video' | 'file' | 'mixed'
type Context = {
    link_count?: number
    target_relation?: 'thread_parent' | 'other' | 'unknown'
    composing?: boolean
    media_kind?: MediaKind
}
export type OperationTrace = { mark: (phase: Phase, context?: Context) => void }
type BridgeWindow = {
    KNOVIGATOR_NATIVE_CAPS?: { operationDiagnostics?: number }
    KNOVIGATOR_OPERATION_DIAGNOSTICS_DISABLED?: boolean
    ReactNativeWebView?: { postMessage: (message: string) => void }
    Knov?: { version?: { gitCommitHash?: string } }
}

const MAX_AGE_MS = 120_000
const WINDOW_MS = 60_000
const MAX_ATTEMPTS = 6
const MAX_EVENTS = 120

/** No timers, storage, document reads or network. All diagnostic failures are swallowed. */
export function createOperationDiagnostics({
    target = () => (typeof window === 'undefined' ? undefined : window),
    now = () => performance.now(),
    random = Math.random,
}: {
    target?: () => BridgeWindow | undefined
    now?: () => number
    random?: () => number
} = {}) {
    let windowStarted = -Infinity
    let attempts = 0
    let events = 0
    let nextId = 0

    const enabledTarget = () => {
        const value = target()
        return value?.KNOVIGATOR_NATIVE_CAPS?.operationDiagnostics === 1 &&
            value.KNOVIGATOR_OPERATION_DIAGNOSTICS_DISABLED !== true &&
            typeof value.ReactNativeWebView?.postMessage === 'function'
            ? value
            : null
    }
    const updateBudget = (time: number) => {
        if (time - windowStarted >= WINDOW_MS) {
            windowStarted = time
            attempts = 0
            events = 0
        }
    }
    const id = () => `diag-${(++nextId).toString(36)}-${random().toString(36).slice(2, 14)}-id`

    return {
        start(
            composer: ComposerKind,
            composerId?: string,
            operation: OperationKind = 'composer_link_preview',
            initialContext: Context = {},
        ): OperationTrace | null {
            try {
                if (!enabledTarget()) return null
                const started = now()
                if (!Number.isFinite(started)) return null
                updateBudget(started)
                if (attempts >= MAX_ATTEMPTS || events >= MAX_EVENTS) return null
                attempts++
                const attemptId = id()
                const safeComposerId = /^[a-zA-Z0-9-]{10,64}$/.test(composerId || '')
                    ? composerId
                    : id()
                const seen = new Set<Phase>()
                let closed = false
                let sequence = 0
                let linkCount = 0
                let relation: Context['target_relation'] = 'unknown'
                let composing: boolean | undefined
                let mediaKind: MediaKind = 'none'
                const trace: OperationTrace = {
                    mark(phase, context = {}) {
                        try {
                            if (closed || seen.has(phase) || !OPERATION_PHASES.includes(phase))
                                return
                            if (
                                phase === 'interaction' &&
                                (operation !== 'composer_link_preview' || !seen.has('embed_commit'))
                            )
                                return
                            const time = now()
                            const elapsed = Math.floor(time - started)
                            if (!Number.isFinite(elapsed) || elapsed < 0 || elapsed > MAX_AGE_MS) {
                                closed = true
                                return
                            }
                            updateBudget(time)
                            if (sequence >= 32 || events >= MAX_EVENTS) return
                            const host = enabledTarget()
                            if (!host) {
                                closed = true
                                return
                            }
                            if (Number.isInteger(context.link_count)) {
                                linkCount = Math.max(0, Math.min(20, context.link_count!))
                            }
                            if (
                                ['thread_parent', 'other', 'unknown'].includes(
                                    context.target_relation || '',
                                )
                            ) {
                                relation = context.target_relation
                            }
                            if (typeof context.composing === 'boolean')
                                composing = context.composing
                            if (
                                context.media_kind &&
                                ['none', 'image', 'video', 'file', 'mixed'].includes(
                                    context.media_kind,
                                )
                            )
                                mediaKind = context.media_kind
                            seen.add(phase)
                            sequence++
                            events++
                            closed = [
                                'interaction',
                                'upload_end',
                                'complete',
                                'blocked',
                                'failed',
                                'cancelled',
                            ].includes(phase)
                            const webCommit = host.Knov?.version?.gitCommitHash
                            host.ReactNativeWebView!.postMessage(
                                JSON.stringify({
                                    kind: 'operationDiagnostic',
                                    version: 1,
                                    operation,
                                    attempt_id: attemptId,
                                    composer_id: safeComposerId,
                                    sequence,
                                    elapsed_ms: elapsed,
                                    phase,
                                    composer: ['modal', 'inline'].includes(composer)
                                        ? composer
                                        : 'unknown',
                                    link_count: linkCount,
                                    ...(operation === 'composer_link_preview'
                                        ? {}
                                        : { media_kind: mediaKind }),
                                    target_relation: relation,
                                    ...(composing === undefined ? {} : { composing }),
                                    ...(typeof webCommit === 'string' &&
                                    /^[a-f0-9]{7,40}$/.test(webCommit)
                                        ? { web_commit: webCommit }
                                        : {}),
                                }),
                            )
                        } catch {
                            // Broken/detached bridge, getters and serialization must not affect editing.
                            closed = true
                        }
                    },
                }
                trace.mark('started', initialContext)
                return trace
            } catch {
                return null
            }
        },
        composerId(): string | undefined {
            try {
                return enabledTarget() ? id() : undefined
            } catch {
                return undefined
            }
        },
    }
}

export const operationDiagnostics = createOperationDiagnostics()
